import React, { useState } from 'react';
import { Product, Ingredient } from '../types';
import { AllergenBadge } from './AllergenBadge';
import { X, Plus, Trash2, AlertCircle } from 'lucide-react';
interface ProductFormProps {
  product?: Product;
  ingredients: Ingredient[];
  onSave: (product: Omit<Product, 'id' | 'createdAt'>) => void;
  onCancel: () => void;
}
export function ProductForm({
  product,
  ingredients,
  onSave,
  onCancel
}: ProductFormProps) {
  const [name, setName] = useState(product?.name || '');
  const [selectedIngredientIds, setSelectedIngredientIds] = useState<string[]>(product?.ingredientIds || []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      ingredientIds: selectedIngredientIds
    });
  };
  const addIngredient = (ingredientId: string) => {
    if (!selectedIngredientIds.includes(ingredientId)) {
      setSelectedIngredientIds([...selectedIngredientIds, ingredientId]);
    }
  };
  const removeIngredient = (ingredientId: string) => {
    setSelectedIngredientIds(selectedIngredientIds.filter(id => id !== ingredientId));
  };
  const selectedIngredients = selectedIngredientIds.map(id => ingredients.find(i => i.id === id)).filter(Boolean) as Ingredient[];
  const availableIngredients = ingredients.filter(i => !selectedIngredientIds.includes(i.id));
  const allAllergens = new Set<string>();
  selectedIngredients.forEach(ingredient => {
    ingredient.allergens.forEach(allergen => allAllergens.add(allergen));
  });
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-id="element-275">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-276">
        <div className="flex items-center justify-between p-6 border-b" data-id="element-277">
          <h2 className="text-xl font-semibold" data-id="element-278">
            {product ? 'Produkt bearbeiten' : 'Neues Produkt'}
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-279">
            <X className="w-5 h-5" data-id="element-280" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" data-id="element-281">
          <div className="p-6 space-y-6" data-id="element-282">
            <div data-id="element-283">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-284">
                Produkt Name
              </label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="z.B. Vollkornbrot, Käsekuchen..." autoFocus required data-id="element-285" />
            </div>

            <div data-id="element-286">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-287">
                Zutaten hinzufügen
              </label>
              {availableIngredients.length > 0 ? <select onChange={e => {
              if (e.target.value) {
                addIngredient(e.target.value);
                e.target.value = '';
              }
            }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-288">
                  <option value="" data-id="element-289">Zutat auswählen...</option>
                  {availableIngredients.map(ingredient => <option key={ingredient.id} value={ingredient.id} data-id="element-290">
                      {ingredient.name}
                      {ingredient.allergens.length > 0 ? ' ⚠️' : ''}
                    </option>)}
                </select> : <p className="text-sm text-gray-500 italic" data-id="element-291">
                  Alle verfügbaren Zutaten wurden hinzugefügt
                </p>}
            </div>

            {selectedIngredients.length > 0 && <div data-id="element-292">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-293">
                  Ausgewählte Zutaten ({selectedIngredients.length})
                </label>
                <div className="space-y-2" data-id="element-294">
                  {selectedIngredients.map(ingredient => <div key={ingredient.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-295">
                      <div className="flex-1" data-id="element-296">
                        <p className="font-medium text-gray-900" data-id="element-297">
                          {ingredient.name}
                        </p>
                        {ingredient.allergens.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-298">
                            {ingredient.allergens.map(allergenId => <AllergenBadge key={allergenId} allergenId={allergenId} size="sm" data-id="element-299" />)}
                          </div>}
                      </div>
                      <button type="button" onClick={() => removeIngredient(ingredient.id)} className="ml-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-id="element-300">
                        <Trash2 className="w-4 h-4" data-id="element-301" />
                      </button>
                    </div>)}
                </div>
              </div>}

            {allAllergens.size > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-302">
                <div className="flex items-start gap-2 mb-3" data-id="element-303">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-304" />
                  <div data-id="element-305">
                    <h3 className="font-medium text-red-900 text-sm" data-id="element-306">
                      Produkt enthält Allergene
                    </h3>
                    <p className="text-xs text-red-700 mt-1" data-id="element-307">
                      Dieses Produkt muss entsprechend gekennzeichnet werden
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-308">
                  {Array.from(allAllergens).map(allergenId => <AllergenBadge key={allergenId} allergenId={allergenId} size="sm" data-id="element-309" />)}
                </div>
              </div>}
          </div>

          <div className="flex gap-3 p-6 border-t bg-gray-50" data-id="element-310">
            <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors font-medium" data-id="element-311">
              Abbrechen
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-312">
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>;
}